// Admin session: login (email + TOTP) yields a short-lived access token (15 min)
// kept in localStorage plus an httpOnly refresh cookie the JS can't read. While
// the admin is active the access token is silently refreshed; after 15 min of
// inactivity refreshing stops, the token expires, and the guard redirects to login.
import { api, setToken, clearToken, getToken, ApiError } from './api'

// The single admin account. Configurable via env, defaults to the known owner.
export const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL ?? 'varun30ec4@gmail.com'

const EXPIRY_KEY = 'admin_token_expiry' // epoch ms when the access token expires

function setExpiry(expiresInSeconds: number) {
  localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresInSeconds * 1000))
}
function getExpiry(): number {
  return Number(localStorage.getItem(EXPIRY_KEY) ?? 0)
}
function clearExpiry() {
  localStorage.removeItem(EXPIRY_KEY)
}

/** ms until the access token expires (negative if already expired). */
export function msUntilExpiry(): number {
  return getExpiry() - Date.now()
}

export async function login(email: string, code: string): Promise<void> {
  const { access_token, expires_in } = await api.login(email.trim(), code.trim())
  setToken(access_token)
  setExpiry(expires_in)
}

/** Exchange the refresh cookie for a new access token. Throws on failure. */
export async function refreshSession(): Promise<void> {
  const { access_token, expires_in } = await api.refresh()
  setToken(access_token)
  setExpiry(expires_in)
}

export async function logout(): Promise<void> {
  try { await api.logout() } catch { /* clear locally regardless */ }
  clearToken()
  clearExpiry()
}

/** Authenticated only if we hold a non-expired access token. */
export function isAuthenticated(): boolean {
  return getToken() !== null && msUntilExpiry() > 0
}

export { ApiError }
